const DEFAULT_WATCH_OPTIONS = {
    shortPollingInterval: 3000,
    longPollingInterval: 10000,
    enableBackgroundPolling: true,
};
/**
 * Creates a composable that watches a resource by polling the server continuously.
 * By default, the polling interval is 'short' when the app is active (in the current tab) and 'long'
 * when the app is in the background (not in the current tab).
 * You can also completely disable background polling by setting `enableBackgroundPolling` to false in the options.
 * @param watchHandler The handler function that watches the resource by querying the server.
 * @param options Options to customize the polling interval.
 */
export function useResourceWatcher(watchHandler, options = DEFAULT_WATCH_OPTIONS) {
    const { shortPollingInterval, longPollingInterval, enableBackgroundPolling } = {
        ...DEFAULT_WATCH_OPTIONS,
        ...options,
    };
    let currentPollingInterval = shortPollingInterval;
    let watchTimeout = null;
    let isEventSetup = false;
    /**
     * Starts watching the resource by polling the server continuously.
     */
    function startWatchingResource() {
        stopWatcher();
        tryWatchResource();
    }
    /**
     * Stops continuously watching the resource.
     */
    function stopWatchingResource() {
        stopWatcher();
    }
    function stopWatcher() {
        if (watchTimeout) {
            clearTimeout(watchTimeout);
            watchTimeout = null;
        }
    }
    async function tryWatchResource() {
        try {
            await watchHandler();
        }
        catch (error) {
            console.warn(error);
        }
        finally {
            if (currentPollingInterval) {
                watchTimeout = setTimeout(() => {
                    tryWatchResource();
                }, currentPollingInterval);
            }
        }
    }
    function setupVisibilityListeners() {
        if (!isEventSetup) {
            isEventSetup = true;
            document.addEventListener("visibilitychange", updateThrottle);
        }
    }
    function updateThrottle() {
        if (document.visibilityState === "visible") {
            currentPollingInterval = shortPollingInterval;
            startWatchingResource();
        }
        else {
            currentPollingInterval = enableBackgroundPolling ? longPollingInterval : undefined;
        }
    }
    setupVisibilityListeners();
    return {
        startWatchingResource,
        stopWatchingResource,
    };
}
